import React, { useState, useCallback } from 'react';
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import { useTranslation } from 'react-i18next';
import { IGetLessonWithVersionsResponse } from "api/models/response/IGetLessonWithVersionsResponse";
import { CloneOptionsBox, HelperText } from "components/views/editor/components/AddLessonVersionDialog.styles";
import { supportedLanguages } from "constants/lessonLanguages";

interface AddLessonVersionDialogProps {
    open: boolean;
    onClose: () => void;
    lesson: IGetLessonWithVersionsResponse;
    onAdd: (language: string) => void;
}

export const AddLessonVersionDialog: React.FC<AddLessonVersionDialogProps> = ({
                                                                      open,
                                                                      onClose,
                                                                      lesson,
                                                                      onAdd
                                                                  }) => {
    const { t } = useTranslation();
    const [newLanguage, setNewLanguage] = useState('');
    const [cloneFrom, setCloneFrom] = useState<string>('');

    const handleAdd = useCallback(() => {
        if (!newLanguage.trim()) return;

        onAdd(newLanguage.toLowerCase());
        setNewLanguage('');
        setCloneFrom('');
    }, [newLanguage, onAdd]);

    const handleClose = useCallback(() => {
        setNewLanguage('');
        setCloneFrom('');
        onClose();
    }, [onClose]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
        >
            <DialogTitle>{t('versionSelector.addNewVersion')}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" gutterBottom>
                    {t('versionSelector.languageCode')}
                </Typography>
                <Select
                    fullWidth
                    value={newLanguage}
                    onChange={(e) => setNewLanguage(e.target.value)}
                >
                    {supportedLanguages.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                            {lang.code.toUpperCase()} - {lang.name}
                        </MenuItem>
                    ))}
                </Select>
                <HelperText>
                    {t('versionSelector.languageCodeHelp')}
                </HelperText>
                <CloneOptionsBox>
                    <Typography variant="body2" gutterBottom>
                        {t('versionSelector.cloneFrom')} ({t('versionSelector.optional')})
                    </Typography>
                    <Select
                        fullWidth
                        value={cloneFrom}
                        onChange={(e) => setCloneFrom(e.target.value)}
                        displayEmpty
                    >
                        <MenuItem value="">
                            <em>{t('versionSelector.startEmpty')}</em>
                        </MenuItem>
                        {lesson.versions.map((version) => (
                            <MenuItem key={version.language} value={version.language} disabled>
                                <Typography color="textSecondary">
                                    {version.language.toUpperCase()} - {t('versionSelector.notAvailable')}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Select>
                    <HelperText>
                        {t('versionSelector.cloneHelp')}
                    </HelperText>
                </CloneOptionsBox>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {t('versionSelector.cancel')}
                </Button>
                <Button
                    onClick={handleAdd}
                    variant="contained"
                    disabled={!newLanguage.trim()}
                >
                    {t('versionSelector.add')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};